import {useState, useRef, useEffect} from 'react'
import {useNavigate} from 'react-router-dom'
import {adminLogin, saveAdminToken} from './_requests'
import {useAdminAuth} from './AdminAuthContext'

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || ''

function TurnstileWidget({onVerify, onExpire}: {onVerify: (token: string) => void, onExpire: () => void}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetIdRef = useRef<string | null>(null)
  const onVerifyRef = useRef(onVerify)
  const onExpireRef = useRef(onExpire)
  const renderedRef = useRef(false)

  onVerifyRef.current = onVerify
  onExpireRef.current = onExpire

  useEffect(() => {
    if (renderedRef.current) return

    const doRender = () => {
      if (!containerRef.current || !(window as any).turnstile || renderedRef.current) return
      renderedRef.current = true
      widgetIdRef.current = (window as any).turnstile.render(containerRef.current, {
        sitekey: TURNSTILE_SITE_KEY,
        callback: (token: string) => onVerifyRef.current(token),
        'expired-callback': () => onExpireRef.current(),
        theme: 'light',
      })
    }

    if ((window as any).turnstile) {
      doRender()
    } else {
      const script = document.createElement('script')
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad&render=explicit'
      script.async = true;
      (window as any).onTurnstileLoad = doRender
      document.head.appendChild(script)
    }

    return () => {
      if (widgetIdRef.current !== null) {
        try { (window as any).turnstile.remove(widgetIdRef.current) } catch {}
        renderedRef.current = false
      }
    }
  }, [])

  return <div ref={containerRef} className="mb-5 d-flex justify-content-center" />
}

export function AdminLogin() {
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [turnstileToken, setTurnstileToken] = useState('')
  const {setAdmin} = useAdminAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (TURNSTILE_SITE_KEY && !turnstileToken) {
      setError('Please complete the CAPTCHA verification')
      return
    }

    setLoading(true)

    try {
      const response = await adminLogin(username, password, turnstileToken)
      if (response.data.success) {
        if (response.data.token) {
          saveAdminToken(response.data.token)
        }
        setAdmin(response.data.admin)
        navigate('/admin/tickets')
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed')
      setTurnstileToken('')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="d-flex flex-column flex-root" style={{minHeight: '100vh', background: '#f5f8fa'}}>
      <div className="d-flex flex-column flex-center flex-column-fluid p-10">
        <div className="card w-lg-500px shadow-sm">
          <div className="card-body p-10 p-lg-15">
            <form onSubmit={handleSubmit} className="form w-100">
              <div className="text-center mb-10">
                <h1 className="text-dark mb-3">Admin Login</h1>
                <div className="text-gray-400 fw-semibold fs-4">Ticket Management System</div>
              </div>

              {error && (
                <div className="alert alert-danger d-flex align-items-center p-5 mb-10">
                  <i className="bi bi-exclamation-circle text-danger fs-2 me-4"></i>
                  <div className="d-flex flex-column">
                    <span>{error}</span>
                  </div>
                </div>
              )}

              <div className="fv-row mb-10">
                <label className="form-label fs-6 fw-bold text-dark">Username</label>
                <input
                  type="text"
                  className="form-control form-control-lg form-control-solid"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>

              <div className="fv-row mb-10">
                <label className="form-label fw-bold text-dark fs-6">Password</label>
                <input
                  type="password"
                  className="form-control form-control-lg form-control-solid"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </div>

              {TURNSTILE_SITE_KEY && (
                <TurnstileWidget
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken('')}
                />
              )}

              <div className="text-center">
                <button
                  type="submit"
                  className="btn btn-lg btn-primary w-100 mb-5"
                  disabled={loading || (!!TURNSTILE_SITE_KEY && !turnstileToken)}
                >
                  {loading ? (
                    <span className="indicator-progress d-block">
                      Please wait...
                      <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                    </span>
                  ) : (
                    <span className="indicator-label">Sign In</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}
